import { ReactElement, ReactNode, useMemo } from 'react';
import { formatPacingNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { Avatar } from '@Components/Avatar';
import { Img } from '@Components/Img';
import { PacingPane } from '@Pages/Pacing/components/PacingPane';
import { PacingItem, SimplicityKey, SummaryData } from './streaming-pacing-types';
import { computeMonthSummary } from './streaming-pacing-utils';

interface StreamingPacingPaneProps<T extends PacingItem> {
  filter?: ReactNode;
  items?: T[];
  kpis?: { label: string; value: string }[];
  labelRenderer: (item: T) => ReactNode;
  lineItemRenderer?: (item: T | (SummaryData & { independent: true })) => ReactNode;
  onSelect: (selection?: string) => void;
  selectedKey?: string;
  simplicityKey?: SimplicityKey;
  summaryData?: SummaryData;
  title?: string;
}

const NO_LOGO_AVATAR_SIZE = 28;

// For the current full month, how much per day has been spent and how much per day is still
// needed to hit the booked spend.
const useMonthSummary = (summaryData?: SummaryData, simplicityKey?: SimplicityKey) =>
  useMemo(() => computeMonthSummary(summaryData, simplicityKey), [summaryData, simplicityKey]);

// The shared PacingPane plus Streaming's month-to-date KPI cards and its "Total" line.
export const StreamingPacingPane = <T extends PacingItem>({
  filter,
  kpis,
  lineItemRenderer,
  simplicityKey,
  summaryData,
  ...rest
}: StreamingPacingPaneProps<T>): ReactElement => {
  const monthSummary = useMonthSummary(summaryData, simplicityKey);
  const monthKpis = monthSummary
    ? [
        {
          label: 'Average Daily Spend',
          value: formatPacingNumber(monthSummary.averageDailySpend, '$')
        },
        {
          label: 'Total Spend Required To Hit Budget',
          value: formatPacingNumber(monthSummary.totalSpendRequiredToHitBudget, '$')
        },
        {
          label: 'Daily Spend Required To Hit Budget',
          value: formatPacingNumber(monthSummary.dailySpendRequiredToHitBudget, '$')
        }
      ]
    : undefined;
  return (
    <PacingPane
      {...rest}
      kpis={kpis || monthKpis ? [...(kpis ?? []), ...(monthKpis ?? [])] : undefined}
      summary={summaryData && lineItemRenderer?.({ ...summaryData, independent: true })}
      controls={filter}
      lineItemRenderer={lineItemRenderer}
    />
  );
};

export const makeNetworkLabel = ({
  network,
  platform,
  description
}: {
  network?: string;
  platform?: string;
  description?: string;
}): ReactElement => {
  let label = (platform ?? '').replace('Streaming ', '').replace('Display ', '');
  if (description && network !== description) {
    label = `${description} ${label}`;
  }
  return (
    <div
      className='streaming-pacing-bar-label streaming-pacing-network-label'
      data-testid={`network-label-${network}`}
    >
      <div className='streaming-pacing-logo network'>
        <Img
          src={`https://cdn.blisspointmedia.com/networks/${network}.png`}
          title={network}
          unloader={<Avatar name={network ?? ''} size={NO_LOGO_AVATAR_SIZE} />}
        />
      </div>
      <span className='streaming-pacing-sub-label'>{label}</span>
    </div>
  );
};

export const makeCompanyLabel = ({ company = '' }: { company?: string }): ReactElement => (
  <div className='streaming-pacing-bar-label' data-testid={`company-label-${company}`}>
    <div className='streaming-pacing-logo company'>
      <Img
        src={`https://cdn.blisspointmedia.com/companies/${company}/logo.png`}
        title={company}
        unloader={
          <div className='streaming-pacing-no-logo'>
            <Avatar name={company} size={NO_LOGO_AVATAR_SIZE} />
            <span className='streaming-pacing-no-logo-name'>{company}</span>
          </div>
        }
      />
    </div>
  </div>
);
